import { StandardEvents } from '@shopify/events';
import { lockScroll, unlockScroll } from '@theme/utilities';

const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 200;
const mobileQuery = window.matchMedia('(max-width: 1279px)'); // burger header, see kh-header.liquid

/**
 * Khartiia Rechova header: bar-as-search with predictive results, mega menus,
 * full-screen mobile menu, cart count kept in sync with Horizon's cart events.
 */
class KhHeader extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {AbortController | null} */
  #searchAbort = null;
  /** @type {number | undefined} */
  #searchTimer;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {HTMLElement | null} */
  #returnFocus = null;

  connectedCallback() {
    const { signal } = this.#abort;

    this.searchPanel = this.querySelector('[data-kh-search]');
    this.results = this.querySelector('[data-kh-search-results]');
    this.initialTemplate = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-kh-search-initial]'));
    this.overlay = this.querySelector('[data-kh-overlay]');
    this.menu = /** @type {HTMLDialogElement | null} */ (this.querySelector('#KhMenu'));
    this.inputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-kh-search-input]')]);

    this.addEventListener('click', this.#onClick, { signal });
    this.burger = this.querySelector('[data-kh-menu-open]');
    this.menu?.addEventListener(
      'close',
      () => {
        unlockScroll(this.menu);
        this.burger?.setAttribute('aria-expanded', 'false');
      },
      { signal }
    );
    this.inputs.forEach((input) => input.addEventListener('input', () => this.#onInput(input), { signal }));
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    mobileQuery.addEventListener('change', () => this.closeAll(), { signal });
    document.addEventListener('kh:search-open', () => this.openSearch(), { signal });

    this.#observeHeight();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#searchAbort?.abort();
    this.#resizeObserver?.disconnect();
    this.#unlockScroll();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target).closest(
      '[data-kh-search-open],[data-kh-search-close],[data-kh-mega],[data-kh-overlay],[data-kh-menu-open],[data-kh-menu-close],[data-kh-cart]'
    );
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-kh-search-toggle]') && this.classList.contains('is-searching')) {
      event.preventDefault();
      this.closeSearch({ clear: true });
    } else if (target.matches('[data-kh-search-open]')) {
      event.preventDefault();
      this.openSearch(target);
    } else if (target.matches('[data-kh-search-close]')) {
      this.closeSearch({ clear: true });
    } else if (target.matches('[data-kh-mega]')) {
      this.toggleMega(target);
    } else if (target.matches('[data-kh-overlay]')) {
      this.closeAll();
    } else if (target.matches('[data-kh-menu-open]')) {
      // The burger toggles: the menu opens under the header bar (non-modal), the bar stays
      if (this.menu?.open && !this.menu.classList.contains('is-closing')) {
        this.#closeMenu();
      } else if (this.menu) {
        if (this.classList.contains('is-searching')) this.closeSearch({ clear: true, restoreFocus: false, instant: true });
        this.#returnFocus = target;
        this.menu.classList.remove('is-closing');
        const bar = this.querySelector('.kh-header__bar');
        this.menu.style.setProperty('--kh-menu-top', `${Math.round(bar?.getBoundingClientRect().bottom ?? 56)}px`);
        if (!this.menu.open) this.menu.show();
        target.setAttribute('aria-expanded', 'true');
        this.menu.focus({ preventScroll: true });
        lockScroll(this.menu);
      }
    } else if (target.matches('[data-kh-menu-close]')) {
      this.#closeMenu();
    } else if (target.matches('[data-kh-cart]')) {
      this.#openCart(event);
    }
  };

  /**
   * The menu slides back out (kh-menu-out, --kh-panel-out) before the dialog closes; another panel taking its place
   * (search, cart) closes it at once.
   */
  #closeMenu() {
    const menu = this.menu;
    if (!menu?.open || menu.classList.contains('is-closing')) return;
    const done = () => {
      if (!menu.classList.contains('is-closing')) return;
      menu.classList.remove('is-closing');
      menu.close();
    };
    menu.classList.add('is-closing');
    this.burger?.setAttribute('aria-expanded', 'false');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return done();
    menu.addEventListener('animationend', (event) => event.target === menu && done(), { once: true });
    window.setTimeout(done, 420); // hidden tab: no animationend
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    // The menu is a non-modal dialog (no native «cancel»): Esc closes it and returns focus to the burger
    if (this.menu?.open && !this.menu.classList.contains('is-closing')) {
      event.preventDefault();
      this.#closeMenu();
      this.burger?.focus();
      return;
    }
    if (this.classList.contains('is-searching') || this.querySelector('[data-kh-mega][aria-expanded="true"]')) {
      event.preventDefault();
      this.closeAll({ restoreFocus: true });
    }
  };

  /* ---------- Search ---------- */

  /** @param {HTMLElement} [opener] */
  openSearch(opener) {
    this.menu?.classList.remove('is-closing');
    if (this.menu?.open) this.menu.close();
    this.closeMega();
    this.#returnFocus = opener && !this.menu?.contains(opener) ? opener : this.#returnFocus;

    this.classList.add('is-searching');
    if (this.searchPanel) {
      this.searchPanel.classList.remove('is-closing');
      const bar = this.querySelector('.kh-header__bar');
      this.searchPanel.style.setProperty('--kh-search-top', `${Math.round(bar?.getBoundingClientRect().bottom ?? 56)}px`);
      this.searchPanel.hidden = false;
    }
    this.querySelector('[data-kh-search-toggle]')?.setAttribute('aria-expanded', 'true');
    this.#setOverlay(true);
    if (mobileQuery.matches) this.#lockScroll();

    const input = mobileQuery.matches ? this.inputs.find((i) => i.id === 'KhSearchInputMobile') : this.inputs[0];
    // Phone: focus once the panel has slid in — a field focused while it moves makes iOS scroll / zoom to it
    const panel = this.searchPanel;
    if (mobileQuery.matches && panel && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let focused = false;
      const focus = () => {
        if (focused || !this.classList.contains('is-searching')) return;
        focused = true;
        input?.focus({ preventScroll: true });
      };
      panel.addEventListener('animationend', (event) => event.target === panel && focus(), { once: true });
      window.setTimeout(focus, 520);
    } else {
      requestAnimationFrame(() => input?.focus({ preventScroll: true }));
    }
  }

  /** @param {{ clear?: boolean, restoreFocus?: boolean, instant?: boolean }} [options] */
  closeSearch({ clear = false, restoreFocus = true, instant = false } = {}) {
    if (!this.classList.contains('is-searching')) return;
    this.classList.remove('is-searching');
    this.querySelector('[data-kh-search-toggle]')?.setAttribute('aria-expanded', 'false');
    const panel = this.searchPanel;
    if (panel) {
      // Phone: the panel slides back out under the bar (kh-menu-out), then hides; desktop and «another panel takes
      // its place» — at once
      const animate =
        !instant && mobileQuery.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!animate) {
        panel.classList.remove('is-closing');
        panel.hidden = true;
      } else {
        const hide = () => {
          if (!panel.classList.contains('is-closing')) return;
          panel.classList.remove('is-closing');
          if (!this.classList.contains('is-searching')) panel.hidden = true;
        };
        panel.classList.add('is-closing');
        panel.addEventListener('animationend', (event) => event.target === panel && hide(), { once: true });
        window.setTimeout(hide, 420); // hidden tab: no animationend
      }
    }
    this.#setOverlay(false);
    this.#unlockScroll();

    if (clear) {
      // A pending or in-flight suggestion would otherwise fill the panel after it was cleared
      clearTimeout(this.#searchTimer);
      this.#searchAbort?.abort();
      this.inputs.forEach((input) => (input.value = ''));
      this.#showInitial();
    }
    if (restoreFocus && this.#returnFocus) {
      // Focus goes back where it was, but without the focus ring: closing with Esc is not «navigating to» the bar.
      const target = this.#returnFocus;
      target.setAttribute('data-kh-quiet-focus', '');
      target.addEventListener('blur', () => target.removeAttribute('data-kh-quiet-focus'), { once: true });
      target.focus({ preventScroll: true, focusVisible: false });
    }
  }

  /** @param {HTMLInputElement} source */
  #onInput(source) {
    this.inputs.forEach((input) => {
      if (input !== source) input.value = source.value;
    });

    clearTimeout(this.#searchTimer);
    const terms = source.value.trim();
    if (terms.length < SEARCH_MIN_CHARS) {
      this.#searchAbort?.abort();
      this.#showInitial();
      return;
    }
    this.#searchTimer = window.setTimeout(() => this.#search(terms), SEARCH_DEBOUNCE_MS);
  }

  /** @param {string} terms */
  async #search(terms) {
    this.#searchAbort?.abort();
    this.#searchAbort = new AbortController();

    const url = new URL(this.dataset.suggestUrl || '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][prefix]', 'last');
    url.searchParams.set('section_id', 'kh-search');

    const skeleton = window.setTimeout(() => this.results?.classList.add('is-loading'), 300);
    try {
      const response = await fetch(url, { signal: this.#searchAbort.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-kh-search-content]');
      if (content && this.results) this.results.replaceChildren(...content.childNodes);
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') console.error(error);
    } finally {
      window.clearTimeout(skeleton);
      this.results?.classList.remove('is-loading');
    }
  }

  #showInitial() {
    if (!this.results || !this.initialTemplate) return;
    this.results.replaceChildren(this.initialTemplate.content.cloneNode(true));
  }

  /* ---------- Mega menu ---------- */

  /** @param {HTMLElement} button */
  toggleMega(button) {
    const isOpen = button.getAttribute('aria-expanded') === 'true';
    this.closeSearch({ restoreFocus: false });
    this.closeMega();
    if (isOpen) return;

    const panel = document.getElementById(button.getAttribute('aria-controls') || '');
    if (!panel) return;
    button.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.#setOverlay(true);
    this.#returnFocus = button;
  }

  closeMega() {
    this.querySelectorAll('[data-kh-mega][aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(button.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = true;
    });
    if (!this.classList.contains('is-searching')) this.#setOverlay(false);
  }

  /** @param {{ restoreFocus?: boolean }} [options] */
  closeAll({ restoreFocus = false } = {}) {
    const hadMega = !!this.querySelector('[data-kh-mega][aria-expanded="true"]');
    this.closeSearch({ clear: true, restoreFocus });
    this.closeMega();
    if (hadMega && restoreFocus) this.#returnFocus?.focus();
  }

  /** @param {boolean} visible */
  #setOverlay(visible) {
    if (this.overlay) this.overlay.hidden = !visible;
  }

  /* ---------- Cart ---------- */

  /** @param {MouseEvent} event */
  #openCart(event) {
    const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.getElementById('cart-drawer'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    this.menu?.classList.remove('is-closing');
    if (this.menu?.open) this.menu.close();
    drawer.open();
  }

  /** @param {Event & { promise?: Promise<{ cart?: { totalQuantity?: number } }> }} event */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart } = {}) => {
        if (typeof cart?.totalQuantity === 'number') this.#setCartCount(cart.totalQuantity);
      })
      .catch(() => {});
  };

  /** @param {number} count */
  #setCartCount(count) {
    this.querySelectorAll('[data-kh-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      /** @type {HTMLElement} */ (badge).hidden = count === 0;
    });

    const i18n = this.#i18n();
    const forms = i18n?.cartCount;
    if (!forms) return;
    const rule = new Intl.PluralRules(document.documentElement.lang || 'uk').select(count);
    const label = (forms[rule] || forms.other || '').replace('{{ count }}', String(count));
    this.querySelectorAll('[data-kh-cart]').forEach((link) => link.setAttribute('aria-label', label));
  }

  /** @returns {{ cartCount?: Record<string, string> } | null} */
  #i18n() {
    try {
      return JSON.parse(this.querySelector('script[data-kh-i18n]')?.textContent || 'null');
    } catch {
      return null;
    }
  }

  /* ---------- Layout ---------- */

  /** Horizon reads --header-height / --header-group-height for sticky elements and drawers. */
  #observeHeight() {
    const group = document.getElementById('header-group');
    const update = () => {
      const bar = this.querySelector('.kh-header__bar');
      const headerHeight = bar instanceof HTMLElement ? bar.offsetHeight : this.offsetHeight;
      document.body.style.setProperty('--header-height', `${headerHeight}px`);
      if (group) {
        const groupHeight = [...group.children].reduce(
          (sum, child) => sum + (child instanceof HTMLElement ? child.offsetHeight : 0),
          0
        );
        document.body.style.setProperty('--header-group-height', `${groupHeight || headerHeight}px`);
      }
    };
    this.#resizeObserver = new ResizeObserver(update);
    this.#resizeObserver.observe(this);
    if (group) [...group.children].forEach((child) => this.#resizeObserver?.observe(child));
    update();
  }

  #lockScroll() {
    lockScroll(this);
  }

  #unlockScroll() {
    unlockScroll(this);
  }
}

if (!customElements.get('kh-header')) customElements.define('kh-header', KhHeader);

/**
 * Account menu (native popover #KhAccountMenu). Light dismiss alone is not enough here: an item that
 * opens a new tab («Telegram-бот · Підключити») leaves the menu open when the person comes back, and
 * other scripts may swallow the outside click. So: close after choosing an item, on an outside click,
 * on scroll and when the tab is left. Delegated on the document and looked up on every event — the
 * header can be re-rendered, which replaces the menu element.
 */
(() => {
  const openMenu = () => {
    const menu = document.getElementById('KhAccountMenu');
    if (!menu || typeof menu.hidePopover !== 'function') return null;
    try {
      return menu.matches(':popover-open') ? menu : null;
    } catch {
      return null;
    }
  };
  const close = () => openMenu()?.hidePopover();

  document.addEventListener(
    'click',
    (event) => {
      const menu = openMenu();
      const target = /** @type {HTMLElement} */ (event.target);
      if (!menu) return;
      if (menu.contains(target)) {
        // An item was chosen: let the link/button do its job, then close.
        if (target.closest('a, button')) window.setTimeout(close, 0);
        return;
      }
      if (!target.closest('[popovertarget="KhAccountMenu"]')) close();
    },
    true
  );
  window.addEventListener('scroll', () => close(), { passive: true, capture: true });
  window.addEventListener('blur', () => close());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') close();
  });
})();
